import { history, useIntl } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Avatar, Drawer, Grid, Spin } from 'antd';
import { createStyles } from 'antd-style';
import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { avatarColor } from '@/pages/user/authShell';
import { CheckGlyph, LogoutGlyph, PlusGlyph, UserGlyph } from '@/pages/user/authIcons';
import HeaderDropdown from '../HeaderDropdown';
import { useMenuStyles } from './menuStyles';

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      overflow: 'hidden',
      alignItems: 'center',
      gap: 8,
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
    trigger: {
      flex: 'none',
      fontWeight: 600,
      // ProLayout 会给头像位置套自己的一层样式，用 antd Avatar 比裸 span 稳
      '.ant-avatar-string': {
        fontSize: 12,
      },
    },
  };
});

const ACCOUNT_PREFIX = 'account:';

const initial = (value: string | null | undefined) =>
  (value ?? '?').trim().charAt(0).toUpperCase();

export const AvatarDropdown: React.FC = () => {
  const { styles } = useStyles();
  const { styles: menu } = useMenuStyles();
  const { user, accounts, isLoading, logout, switchAccount } = useAuth();
  const intl = useIntl();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [sheetOpen, setSheetOpen] = useState(false);

  const t = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

  const select = (key: string) => {
    setSheetOpen(false);

    if (key.startsWith(ACCOUNT_PREFIX)) {
      const userId = Number(key.slice(ACCOUNT_PREFIX.length));
      if (userId !== user?.id) {
        switchAccount(userId);
        // 换了账号，权限和数据都不一样了，回首页重新取。
        history.push('/my-platforms');
      }
      return;
    }

    if (key === 'add-account') {
      // add=1 让 AuthBoundary 别把已登录的人从登录页弹走。
      history.push('/user/login?add=1');
      return;
    }

    if (key === 'logout') {
      // 退出的是当前账号；还有别的账号就顶上，不用回登录页。
      const next = logout();
      history.replace(next ? '/my-platforms' : '/user/login');
      return;
    }

    if (key === 'profile') {
      history.push('/profile');
    }
  };

  if (isLoading) {
    return (
      <span className={styles.action}>
        <Spin size="small" style={{ marginLeft: 8, marginRight: 8 }} />
      </span>
    );
  }

  if (!user) {
    return null;
  }

  const displayName = user.fullName ?? user.username;
  const signedIn = accounts.filter((account) => account.user?.id);

  const accountRow = (account: (typeof signedIn)[number]) => ({
    id: account.user?.id as number,
    name: account.user?.fullName ?? account.user?.username,
    mail: account.user?.email ?? account.user?.username,
    color: avatarColor(account.user?.username ?? account.user?.id),
    letter: initial(account.user?.fullName ?? account.user?.username),
    current: account.user?.id === user.id,
  });

  const trigger = (
    <span className={styles.action}>
      <Avatar
        size={28}
        className={styles.trigger}
        style={{ background: avatarColor(user.username ?? user.id) }}
      >
        {initial(displayName)}
      </Avatar>
      {/* 手机上只留头像：名字一长就把顶栏挤爆，点开的面板里本来就写着是谁 */}
      {isMobile ? null : displayName}
    </span>
  );

  if (isMobile) {
    return (
      <>
        {/* 手机上不用下拉：锚在右上角的浮层会被屏幕边缘裁掉，
            改成从底部升起的面板，触点也更大 —— Google 在手机上就是这么做的。 */}
        <span onClick={() => setSheetOpen(true)}>{trigger}</span>
        <Drawer
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          placement="bottom"
          height="auto"
          closable={false}
          styles={{ body: { padding: 0 }, content: { borderRadius: '16px 16px 0 0' } }}
        >
          <div className={menu.sheetBody}>
            <div className={menu.sheetGroup}>{t('menu.account.signedIn', '已登录账号')}</div>

            {signedIn.map((account) => {
              const row = accountRow(account);
              return (
                <button
                  type="button"
                  key={row.id}
                  className={menu.sheetRow}
                  onClick={() => select(`${ACCOUNT_PREFIX}${row.id}`)}
                >
                  <span className={menu.avatar} style={{ background: row.color }}>
                    {row.letter}
                  </span>
                  <span className={menu.accountText}>
                    <span className={menu.name}>{row.name}</span>
                    <span className={menu.mail}>{row.mail}</span>
                  </span>
                  {row.current ? <CheckGlyph className={menu.tick} /> : null}
                </button>
              );
            })}

            <div className={menu.sheetDivider} />

            <button type="button" className={menu.sheetRow} onClick={() => select('add-account')}>
              <PlusGlyph />
              {t('menu.account.add', '添加其他账号')}
            </button>
            <button type="button" className={menu.sheetRow} onClick={() => select('profile')}>
              <UserGlyph />
              {t('menu.account.profile', '个人资料')}
            </button>
            <button type="button" className={menu.sheetRow} onClick={() => select('logout')}>
              <LogoutGlyph />
              {signedIn.length > 1
                ? t('menu.account.logoutCurrent', '退出当前账号')
                : t('menu.account.logout', '退出登录')}
            </button>
          </div>
        </Drawer>
      </>
    );
  }

  const menuItems: MenuProps['items'] = [
    {
      key: 'accounts',
      type: 'group',
      label: t('menu.account.signedIn', '已登录账号'),
      children: signedIn.map((account) => {
        const row = accountRow(account);
        return {
          key: `${ACCOUNT_PREFIX}${row.id}`,
          label: (
            <span className={menu.account}>
              <span className={menu.avatar} style={{ background: row.color }}>
                {row.letter}
              </span>
              <span className={menu.accountText}>
                <span className={menu.name}>{row.name}</span>
                <span className={menu.mail}>{row.mail}</span>
              </span>
              {row.current ? <CheckGlyph className={menu.tick} /> : null}
            </span>
          ),
        };
      }),
    },
    { type: 'divider' },
    {
      key: 'add-account',
      icon: <PlusGlyph />,
      label: t('menu.account.add', '添加其他账号'),
    },
    {
      key: 'profile',
      icon: <UserGlyph />,
      label: t('menu.account.profile', '个人资料'),
    },
    {
      key: 'logout',
      icon: <LogoutGlyph />,
      label: signedIn.length > 1
        ? t('menu.account.logoutCurrent', '退出当前账号')
        : t('menu.account.logout', '退出登录'),
    },
  ];

  return (
    <HeaderDropdown
      overlayClassName={menu.panel}
      menu={{
        selectedKeys: [],
        onClick: ({ key }) => select(key),
        items: menuItems,
      }}
    >
      {trigger}
    </HeaderDropdown>
  );
};
